import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PasswordField, { pwStrong } from "@/components/PasswordField";

export default function ForgotPassword() {
  const [method, setMethod] = useState("email"); // "email" | "phone"
  const nav = useNavigate();

  // email
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  // phone
  const [phone, setPhone] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);

  const [busy, setBusy] = useState(false);

  const submitEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); } catch { /* generic */ }
    finally { setBusy(false); setSent(true); }
  };

  const sendOtp = async () => {
    if (phone.replace(/\D/g, "").length < 10) return toast.error("Enter a valid 10-digit mobile number.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password-phone", { phone });
      setOtpSent(true);
      toast.success(data.mock ? "If registered, code sent (test mode — check server logs)" : "If your number is registered, a code has been sent.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send code"); }
    finally { setBusy(false); }
  };

  const resetByPhone = async (e) => {
    e.preventDefault();
    if (!pwStrong(pw)) return toast.error("Please choose a stronger password (see the requirements).");
    if (pw !== pw2) return toast.error("Passwords don't match.");
    setBusy(true);
    try { await api.post("/auth/reset-password-phone", { phone, otp, new_password: pw }); setDone(true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't reset password"); }
    finally { setBusy(false); }
  };

  const Tab = ({ k, label }) => (
    <button onClick={() => setMethod(k)} className={`px-4 py-2 border font-mono text-[10px] uppercase tracking-widest ${method === k ? "bg-foreground text-background border-foreground" : "border-foreground/30 hover:border-foreground"}`}>{label}</button>
  );

  return (
    <div className="max-w-[520px] mx-auto px-6 md:px-12 py-20">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Account access</p>
      <h1 className="font-serif text-5xl tracking-tighter">Forgot password.</h1>

      {done ? (
        <div className="mt-8 border border-foreground p-6">
          <p className="font-serif text-2xl tracking-tight">Password reset.</p>
          <p className="mt-2 text-muted-foreground">You can now sign in with your new password.</p>
          <button onClick={() => nav("/login")} className="inline-block mt-5 bg-foreground text-background px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))]">Sign in</button>
        </div>
      ) : (
        <>
          <div className="flex gap-2 mt-6">
            <Tab k="email" label="By email" />
            <Tab k="phone" label="By mobile" />
          </div>

          {method === "email" ? (
            sent ? (
              <div className="mt-6 border border-foreground p-6">
                <p className="font-serif text-2xl tracking-tight">Check your email.</p>
                <p className="mt-2 text-muted-foreground leading-relaxed">If an account exists for <span className="text-foreground">{email}</span>, we've sent a reset link (valid 30 minutes).</p>
                <Link to="/login" className="inline-block mt-5 font-mono text-xs uppercase tracking-widest underline hover:text-[hsl(var(--accent))]">Back to sign in</Link>
              </div>
            ) : (
              <form onSubmit={submitEmail} className="mt-6 border border-foreground p-6 md:p-8 space-y-5">
                <p className="text-muted-foreground text-sm leading-relaxed">Enter your account email and we'll send you a link to reset your password.</p>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Email</p>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="underline-input w-full" />
                </div>
                <button disabled={busy} className="bg-foreground text-background px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">{busy ? "Sending…" : "Send reset link"}</button>
              </form>
            )
          ) : (
            <form onSubmit={resetByPhone} className="mt-6 border border-foreground p-6 md:p-8 space-y-5">
              <p className="text-muted-foreground text-sm leading-relaxed">Enter the mobile number on your account. We'll text you a one-time code to reset your password.</p>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Mobile number</p>
                <div className="flex gap-2 items-center">
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="numeric" placeholder="10-digit mobile" disabled={otpSent} className="underline-input flex-1" />
                  <button type="button" onClick={sendOtp} disabled={busy} className="border border-foreground px-3 py-2 text-[10px] font-mono uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-40 whitespace-nowrap">{otpSent ? "Resend" : "Send code"}</button>
                </div>
              </div>
              {otpSent && (
                <>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">OTP</p>
                    <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit code" className="underline-input w-full" />
                  </div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">New password</p>
                    <PasswordField value={pw} onChange={(e) => setPw(e.target.value)} strength />
                  </div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Confirm new password</p>
                    <PasswordField value={pw2} onChange={(e) => setPw2(e.target.value)} />
                  </div>
                  <button disabled={busy || otp.length < 6 || !pwStrong(pw) || pw !== pw2} className="bg-foreground text-background px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">{busy ? "Resetting…" : "Reset password"}</button>
                </>
              )}
            </form>
          )}

          <p className="mt-6 font-mono text-[11px] text-muted-foreground"><Link to="/login" className="underline hover:text-foreground">Back to sign in</Link></p>
        </>
      )}
    </div>
  );
}
